import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactQuill from 'react-quill';
import {
  createInterview,
  deleteInterview,
  getInterview,
  listInterviews,
  updateInterview,
} from '../../services/interviewService';
import { fetchProfile } from '../../services/profileService';
import { formatProfileFullName, maskCandidateName } from '../../utils/candidateDisplay';
import 'react-quill/dist/quill.bubble.css';
import 'react-quill/dist/quill.snow.css';
import './InterviewQuestions.css';

const emptyDash = (v) => {
  if (v == null) return '—';
  const s = String(v).trim();
  return s || '—';
};

const norm = (s) => (s == null ? '' : String(s).trim().toLowerCase());

function matchesFilters(row, f) {
  const dateStr = row.interviewDate ? String(row.interviewDate) : '';
  if (f.date && !dateStr.toLowerCase().includes(f.date.toLowerCase())) return false;
  if (f.client && !norm(row.client).includes(norm(f.client))) return false;
  if (f.round && !norm(row.interviewRound).includes(norm(f.round))) return false;
  if (f.position && !norm(row.positionTitle).includes(norm(f.position))) return false;
  if (f.interviewer && !norm(row.interviewer).includes(norm(f.interviewer))) return false;
  if (f.candidate && !norm(maskCandidateName(row.candidate)).includes(norm(f.candidate))) return false;
  return true;
}

const emptyForm = () => ({
  interviewDate: '',
  client: '',
  interviewRound: '',
  positionTitle: '',
  interviewer: '',
  candidate: '',
  questions: [''],
});

const QUILL_EDIT_MODULES = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'image', 'code-block', 'clean'],
  ],
};

function RichQuestion({ html }) {
  const value = typeof html === 'string' ? html.trim() : '';
  if (!value) return <p className="iv-records__qempty">Empty question.</p>;
  const isLikelyHtml = /<\/?[a-z][\s\S]*>/i.test(value);
  if (!isLikelyHtml) return <div className="iv-records__qplain">{value}</div>;
  return (
    <div className="iv-records__qrich" dangerouslySetInnerHTML={{ __html: value }} />
  );
}

export default function InterviewQuestions() {
  const ROUND_OPTIONS = ['Client R1', 'Client R2', 'Client R3', 'Client R4', 'Client R5+'];
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [filters, setFilters] = useState({
    date: '',
    client: '',
    round: '',
    position: '',
    interviewer: '',
    candidate: '',
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [defaultUserFullName, setDefaultUserFullName] = useState('');
  const [detailsById, setDetailsById] = useState({});
  const [detailLoadingById, setDetailLoadingById] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listInterviews();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load interview records.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    fetchProfile()
      .then((p) => {
        if (cancelled) return;
        setDefaultUserFullName(formatProfileFullName(p));
      })
      .catch(() => {
        if (!cancelled) setDefaultUserFullName('');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(
    () => rows.filter((r) => matchesFilters(r, filters)),
    [rows, filters]
  );

  const toggleExpand = (id) => {
    setExpandedId((cur) => {
      const next = cur === id ? null : id;
      if (next != null) {
        const alreadyLoaded = Array.isArray(detailsById[id]?.questions);
        const loading = !!detailLoadingById[id];
        if (!alreadyLoaded && !loading) {
          setDetailLoadingById((m) => ({ ...m, [id]: true }));
          getInterview(id)
            .then((full) => {
              setDetailsById((m) => ({ ...m, [id]: full }));
            })
            .catch(() => {
              setDetailsById((m) => ({ ...m, [id]: { questions: [] } }));
            })
            .finally(() => {
              setDetailLoadingById((m) => ({ ...m, [id]: false }));
            });
        }
      }
      return next;
    });
  };

  const openAdd = () => {
    setEditingId(null);
    const fullName = defaultUserFullName || '';
    setForm({
      ...emptyForm(),
      candidate: fullName,
      interviewer: fullName,
    });
    setModalOpen(true);
  };

  const openEdit = (row, detailOverride = null) => {
    const full = detailOverride || detailsById[row.id];
    const source = full && Array.isArray(full.questions) ? { ...row, ...full } : row;
    setEditingId(row.id);
    setForm({
      interviewDate: source.interviewDate || '',
      client: source.client || '',
      interviewRound: source.interviewRound || '',
      positionTitle: source.positionTitle || '',
      interviewer: source.interviewer || '',
      candidate: source.candidate || '',
      questions: Array.isArray(source.questions) && source.questions.length > 0 ? source.questions : [''],
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm(emptyForm());
  };

  const onQuestionChange = (index, value) => {
    setForm((f) => ({
      ...f,
      questions: f.questions.map((q, i) => (i === index ? value : q)),
    }));
  };

  const addQuestionAfter = (index) => {
    setForm((f) => {
      const next = [...f.questions];
      next.splice(index + 1, 0, '');
      return { ...f, questions: next };
    });
  };

  const removeQuestion = (index) => {
    setForm((f) => {
      if (f.questions.length <= 1) return f;
      return { ...f, questions: f.questions.filter((_, i) => i !== index) };
    });
  };

  const onSaveModal = async () => {
    setSaving(true);
    setError('');
    try {
      const questions = (form.questions || [])
        .map((s) => (typeof s === 'string' ? s.trim() : ''))
        .filter(Boolean);
      const payload = {
        interviewDate: form.interviewDate,
        client: form.client.trim(),
        interviewRound: form.interviewRound.trim(),
        positionTitle: form.positionTitle.trim(),
        interviewer: form.interviewer.trim(),
        candidate: form.candidate.trim(),
        questions,
      };
      if (!payload.interviewDate) {
        setError('Date is required.');
        return;
      }
      if (editingId == null) {
        await createInterview(payload);
      } else {
        await updateInterview(editingId, payload);
      }
      closeModal();
      await load();
      setExpandedId(null);
      setDetailsById({});
    } catch (e) {
      setError(
        e.response?.data?.message || e.response?.data?.error || e.message || 'Save failed.'
      );
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Delete interview record for ${maskCandidateName(row.candidate)}?`)) return;
    setDeletingId(row.id);
    setError('');
    try {
      await deleteInterview(row.id);
      if (expandedId === row.id) setExpandedId(null);
      setDetailsById((m) => {
        const n = { ...m };
        delete n[row.id];
        return n;
      });
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Delete failed.');
    } finally {
      setDeletingId(null);
    }
  };

  const onFilterChange = (key) => (e) => {
    setFilters((f) => ({ ...f, [key]: e.target.value }));
  };

  return (
    <div className="iv-records">
      <header className="iv-records__header">
        <div className="iv-records__titles">
          <h1 className="iv-records__title">Interview records</h1>
          <p className="iv-records__lead">Filter columns below. Expand a row to see questions.</p>
        </div>
        <button type="button" className="iv-records__btn-add" onClick={openAdd} disabled={loading}>
          + Add interview
        </button>
      </header>

      {error && <div className="iv-records__error">{error}</div>}

      {loading ? (
        <p className="iv-records__muted">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="iv-records__muted">
          {rows.length === 0 ? 'No interview records yet. Add one to get started.' : 'No rows match your filters.'}
        </p>
      ) : (
        <div className="iv-records__scroll">
          <table className="iv-records__table">
            <thead>
              <tr>
                <th className="iv-records__expand-col" aria-hidden="true" />
                <th>Date</th>
                <th>Client</th>
                <th>Round</th>
                <th>Position</th>
                <th>Interviewer</th>
                <th>Candidate</th>
              </tr>
              <tr className="iv-records__filter-row">
                <th />
                <th>
                  <input
                    className="iv-records__filter"
                    placeholder="Filter…"
                    value={filters.date}
                    onChange={onFilterChange('date')}
                    aria-label="Filter by date"
                  />
                </th>
                <th>
                  <input
                    className="iv-records__filter"
                    placeholder="Filter…"
                    value={filters.client}
                    onChange={onFilterChange('client')}
                    aria-label="Filter by client"
                  />
                </th>
                <th>
                  <input
                    className="iv-records__filter"
                    placeholder="Filter…"
                    value={filters.round}
                    onChange={onFilterChange('round')}
                    aria-label="Filter by round"
                  />
                </th>
                <th>
                  <input
                    className="iv-records__filter"
                    placeholder="Filter…"
                    value={filters.position}
                    onChange={onFilterChange('position')}
                    aria-label="Filter by position"
                  />
                </th>
                <th>
                  <input
                    className="iv-records__filter"
                    placeholder="Filter…"
                    value={filters.interviewer}
                    onChange={onFilterChange('interviewer')}
                    aria-label="Filter by interviewer"
                  />
                </th>
                <th>
                  <input
                    className="iv-records__filter"
                    placeholder="Filter…"
                    value={filters.candidate}
                    onChange={onFilterChange('candidate')}
                    aria-label="Filter by candidate"
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <React.Fragment key={row.id}>
                  <tr
                    className="iv-records__row-main"
                    onClick={() => toggleExpand(row.id)}
                  >
                    <td className="iv-records__expand-col">
                      <span
                        className={`iv-records__chevron ${expandedId === row.id ? 'iv-records__chevron--open' : ''}`}
                      >
                        ▸
                      </span>
                    </td>
                    <td>{emptyDash(row.interviewDate)}</td>
                    <td>{emptyDash(row.client)}</td>
                    <td>{emptyDash(row.interviewRound)}</td>
                    <td>{emptyDash(row.positionTitle)}</td>
                    <td>{emptyDash(row.interviewer)}</td>
                    <td>{maskCandidateName(row.candidate)}</td>
                  </tr>
                  {expandedId === row.id && (
                    <tr className="iv-records__expand-panel">
                      <td colSpan={7}>
                        {detailLoadingById[row.id] ? (
                          <div className="iv-records__expand-card">
                            <p className="iv-records__muted">Loading questions…</p>
                          </div>
                        ) : (
                        (() => {
                          const detail = detailsById[row.id] || row;
                          const qs = Array.isArray(detail.questions) ? detail.questions : [];
                          return (
                        <div className="iv-records__expand-card">
                          <div className="iv-records__expand-head">
                            <p className="iv-records__qlabel">Questions</p>
                            <div className="iv-records__expand-actions iv-records__expand-actions--top">
                              <button
                                type="button"
                                className="iv-records__btn iv-records__btn--edit"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEdit(row, detail);
                                }}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="iv-records__btn iv-records__btn--delete"
                                disabled={deletingId === row.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDelete(row);
                                }}
                              >
                                {deletingId === row.id ? 'Deleting…' : 'Delete'}
                              </button>
                            </div>
                          </div>
                          {qs.length > 0 ? (
                            <div className="iv-records__qlist">
                              {qs.map((q, i) => (
                                <section key={`${row.id}-q-${i}`} className="iv-records__qitem">
                                  <div className="iv-records__qindex">{`Q${i + 1}`}</div>
                                  <div className="iv-records__qcontent">
                                    <RichQuestion html={q} />
                                  </div>
                                </section>
                              ))}
                            </div>
                          ) : (
                            <p className="iv-records__qempty">No questions recorded.</p>
                          )}
                        </div>
                          );
                        })()
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <div
          className="iv-modal-overlay"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="iv-modal" role="dialog" aria-modal="true" aria-labelledby="iv-modal-title">
            <div className="iv-modal__head">
              <h2 id="iv-modal-title" className="iv-modal__title">
                {editingId == null ? 'Add interview' : 'Edit interview'}
              </h2>
              <button type="button" className="iv-modal__close" onClick={closeModal} aria-label="Close">
                ×
              </button>
            </div>
            <div className="iv-modal__body">
              <div className="iv-modal__field">
                <label htmlFor="iv-f-date">Date</label>
                <input
                  id="iv-f-date"
                  type="date"
                  value={form.interviewDate}
                  onChange={(e) => setForm((f) => ({ ...f, interviewDate: e.target.value }))}
                />
              </div>
              <div className="iv-modal__field">
                <label htmlFor="iv-f-client">Client</label>
                <input
                  id="iv-f-client"
                  type="text"
                  value={form.client}
                  onChange={(e) => setForm((f) => ({ ...f, client: e.target.value }))}
                />
              </div>
              <div className="iv-modal__field">
                <label htmlFor="iv-f-round">Round</label>
                <select
                  id="iv-f-round"
                  value={form.interviewRound}
                  onChange={(e) => setForm((f) => ({ ...f, interviewRound: e.target.value }))}
                >
                  <option value="">Select</option>
                  {ROUND_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
              <div className="iv-modal__field">
                <label htmlFor="iv-f-position">Position</label>
                <input
                  id="iv-f-position"
                  type="text"
                  value={form.positionTitle}
                  onChange={(e) => setForm((f) => ({ ...f, positionTitle: e.target.value }))}
                />
              </div>
              <div className="iv-modal__field">
                <label htmlFor="iv-f-interviewer">Interviewer</label>
                <input
                  id="iv-f-interviewer"
                  type="text"
                  value={form.interviewer}
                  onChange={(e) => setForm((f) => ({ ...f, interviewer: e.target.value }))}
                  placeholder="First name Last name"
                />
              </div>
              <div className="iv-modal__field">
                <label htmlFor="iv-f-candidate">Candidate</label>
                {editingId != null ? (
                  <p id="iv-f-candidate" className="iv-modal__masked-candidate">
                    {maskCandidateName(form.candidate)}
                  </p>
                ) : (
                  <>
                    <input
                      id="iv-f-candidate"
                      type="text"
                      value={form.candidate}
                      onChange={(e) => setForm((f) => ({ ...f, candidate: e.target.value }))}
                      placeholder="First name Last name"
                    />
                    <p className="iv-modal__hint">Defaults to your profile name; use first and last name separated by a space.</p>
                  </>
                )}
              </div>
              <div className="iv-modal__field">
                <label htmlFor="iv-f-questions">Questions</label>
                <div className="iv-modal__questions-editors" id="iv-f-questions">
                  {form.questions.map((q, i) => (
                    <div key={`mq-${i}`} className="iv-modal__qrow">
                      <div className="iv-modal__qindex">{`Q${i + 1}`}</div>
                      <div className="iv-modal__qeditor-wrap">
                        <ReactQuill
                          theme="snow"
                          value={q}
                          modules={QUILL_EDIT_MODULES}
                          onChange={(value) => onQuestionChange(i, value)}
                          className="iv-modal__qeditor"
                        />
                      </div>
                      <div className="iv-modal__qactions">
                        <button
                          type="button"
                          className="iv-modal__qbtn iv-modal__qbtn--remove"
                          onClick={() => removeQuestion(i)}
                          disabled={form.questions.length <= 1}
                          aria-label={`Remove question ${i + 1}`}
                        >
                          −
                        </button>
                        <button
                          type="button"
                          className="iv-modal__qbtn iv-modal__qbtn--add"
                          onClick={() => addQuestionAfter(i)}
                          aria-label={`Add question after ${i + 1}`}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="iv-modal__hint">Rich text supported: bold/italic, links, images, code blocks, lists.</p>
              </div>
            </div>
            <div className="iv-modal__foot">
              <button type="button" className="iv-modal__cancel" onClick={closeModal} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="iv-modal__save" onClick={onSaveModal} disabled={saving}>
                {saving ? 'Saving…' : editingId == null ? 'Submit' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
